// #tag: USER REGISTRATION SCREEN
// #purpose: Onboards new students and creates user profiles with email/password credentials or Google SSO.
import React, { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Mail, User } from 'lucide-react';
import { Illustration } from '../components/common/Illustration';
import { GoogleSignInButton } from '../components/auth/GoogleSignInButton';
import { PasswordField } from '../components/auth/PasswordField';
import { useAuth } from '../context/AuthProvider';
import { getPostLoginPath } from '../lib/authRedirect';

function postRegisterPath(from?: string, role?: string | null) {
  return getPostLoginPath(from, role);
}

export function RegisterScreen() {
  const { register, status, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (status === 'authenticated' && user) {
    return <Navigate to={postRegisterPath(from, user.role)} replace />;
  }

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const me = await register(name, email, password);
      navigate(postRegisterPath(from, me.role), { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="adaptive-page auth-screen">
      <div className="auth-layout">
        <Illustration context="onboarding.auth" size="md" className="auth-screen-illustration" />
        <div className="auth-card glass-surface">
          <span className="section-kicker">Create account</span>
          <h1>Join WebFoxx Results</h1>
          <p>Save exams and get personalized updates — ad-free.</p>

          {error && <div className="auth-error" role="alert">{error}</div>}

          <form className="auth-form" onSubmit={onSubmit}>
            <label className="auth-field">
              <User size={17} aria-hidden="true" />
              <input
                type="text"
                placeholder="Full name"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="auth-field">
              <Mail size={17} aria-hidden="true" />
              <input
                type="email"
                placeholder="Email address"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <PasswordField
              label="Password"
              value={password}
              onChange={setPassword}
              placeholder="Password (min 6 characters)"
              autoComplete="new-password"
              minLength={6}
            />
            <button type="submit" className="primary-action" disabled={loading}>
              {loading ? 'Creating account…' : 'Create account'}
            </button>
          </form>

          <GoogleSignInButton onSignedIn={(signedInUser) => navigate(postRegisterPath(from, signedInUser.role), { replace: true })} />

          <div className="auth-links">
            <span>Already have an account? <Link to="/login" state={{ from }}>Sign in</Link></span>
          </div>
        </div>
      </div>
    </section>
  );
}
